import type { ApplicantCard, Stage } from '@/hooks/use-opportunity-applicants'
import { sortApplicantsByBoardPosition } from '@/lib/board-position'

export const INVITED_PIPELINE_COLUMN_ID = 'invited'
export const UNASSIGNED_PIPELINE_COLUMN_ID = '__unassigned__'

export const INVITED_PIPELINE_STAGE: Stage = {
  slug: INVITED_PIPELINE_COLUMN_ID,
  name: 'Invited',
  order: -2,
  is_terminal: false,
}

export const UNASSIGNED_PIPELINE_STAGE: Stage = {
  slug: UNASSIGNED_PIPELINE_COLUMN_ID,
  name: 'No stage',
  order: -1,
  is_terminal: false,
}

export function pipelineColumnSlug(applicant: ApplicantCard): string {
  if (isInvitedPipelineApplicant(applicant)) return INVITED_PIPELINE_COLUMN_ID
  return applicant.current_stage?.slug ?? UNASSIGNED_PIPELINE_COLUMN_ID
}

/** Stage columns rendered after the dedicated Invited section (excludes global invited template). */
export function boardPipelineStages(stages: Stage[]): Stage[] {
  return stages.filter((stage) => stage.slug !== INVITED_PIPELINE_COLUMN_ID)
}

export function isInvitedPipelineApplicant(applicant: ApplicantCard): boolean {
  return (
    applicant.status === 'invited' ||
    applicant.current_stage?.slug === INVITED_PIPELINE_COLUMN_ID
  )
}

export function partitionPipelineApplicants(applicants: ApplicantCard[]) {
  const sorted = sortApplicantsByBoardPosition(applicants)
  const invited: ApplicantCard[] = []
  const pipeline: ApplicantCard[] = []
  for (const applicant of sorted) {
    if (isInvitedPipelineApplicant(applicant)) invited.push(applicant)
    else pipeline.push(applicant)
  }
  return { invited, pipeline }
}

export function groupPipelineApplicantsByStage(pipeline: ApplicantCard[], stages: Stage[]) {
  const grouped = new Map<string, ApplicantCard[]>()
  for (const stage of stages) {
    grouped.set(stage.slug, [])
  }
  grouped.set(UNASSIGNED_PIPELINE_COLUMN_ID, [])
  for (const applicant of pipeline) {
    const key = applicant.current_stage?.slug ?? UNASSIGNED_PIPELINE_COLUMN_ID
    if (!grouped.has(key)) grouped.set(key, [])
    grouped.get(key)!.push(applicant)
  }
  for (const [key, list] of grouped) {
    grouped.set(key, sortApplicantsByBoardPosition(list))
  }
  return grouped
}

export function buildPipelineSortColumns(applicants: ApplicantCard[], stages: Stage[]) {
  const { invited, pipeline } = partitionPipelineApplicants(applicants)
  const byStage = groupPipelineApplicantsByStage(pipeline, stages)
  const columns: Array<{ items: ApplicantCard[] }> = []

  if (invited.length > 0) {
    columns.push({ items: sortApplicantsByBoardPosition(invited) })
  }

  for (const stage of boardPipelineStages(stages)) {
    const list = byStage.get(stage.slug) ?? []
    if (list.length > 0) columns.push({ items: list })
  }

  const unassigned = byStage.get(UNASSIGNED_PIPELINE_COLUMN_ID) ?? []
  if (unassigned.length > 0) {
    columns.push({ items: unassigned })
  }

  return columns
}
